import type { AuthorBylineConfig } from 'interfaces/content/AuthorByline';
import { useState } from 'react';
import useBreakpoint from 'hooks/useBreakpoint';
import { COMMON_TEST_IDS } from 'constants/testsIds/common';
import { withErrorBoundary } from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import { Contributor, ContributorImage, ContributorName } from 'components/Contributors/Contributor';
import * as S from './ContributorsBylineStyles';

const CompactByline = ({ authors, reviewedBy, trackingData, dataCy }: AuthorBylineConfig) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [firstAuthor, ...hiddenAuthors] = authors;
  const visibleContributors = reviewedBy ? [firstAuthor, reviewedBy] : authors.slice(0, 2);
  const allContributors = reviewedBy ? visibleContributors.concat(hiddenAuthors) : authors;
  const showMoreContributors = allContributors.length > 2;

  return (
    <S.CompactBylineWrapper data-cy={dataCy}>
      <S.CollapsedView $isVisible={!isExpanded}>
        <button
          type='button'
          className='contributors-images'
          onClick={() => setIsExpanded(!isExpanded)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') setIsExpanded(!isExpanded);
          }}
          tabIndex={0}
        >
          {visibleContributors.map((contributor) => (
            <ContributorImage
              key={contributor.uri}
              contributorName={contributor.name}
              image={contributor.image}
            />
          ))}
        </button>
        <div>
          {visibleContributors.map((contributor, index) => (
            <>
              {index === 1 && ', '}
              <ContributorName
                contributor={contributor}
                type={reviewedBy && index === 1 ? 'reviewer' : 'author'}
              />
            </>
          ))}
        </div>
      </S.CollapsedView>
      <S.ExpandedView $isVisible={isExpanded}>
        {allContributors.map((contributor, index) => (
          <Contributor
            key={contributor.uri}
            contributor={contributor}
            type={reviewedBy && index === 1 ? 'reviewer' : 'author'}
            trackingData={trackingData}
          />
        ))}
      </S.ExpandedView>
      {(showMoreContributors || isExpanded) && (
        <S.SeeMoreButton
          onClick={() => setIsExpanded(!isExpanded)}
          type='button'
          variant='tertiaryBrand'
          arrowType={isExpanded ? 'up-arrow' : 'down-arrow'}
          data-cy={COMMON_TEST_IDS.SEE_MORE_AUTHORS_BUTTON}
        >
          {isExpanded ? 'SEE LESS' : 'SEE MORE'}
        </S.SeeMoreButton>
      )}
    </S.CompactBylineWrapper>
  );
};
const DesktopByline = ({ authors, trackingData, dataCy, reviewedBy }: AuthorBylineConfig) => {
  const [isOpen, setIsOpen] = useState(false);
  const visibleContributors = reviewedBy ? [...authors.slice(0, 2), reviewedBy] : authors.slice(0, 3);
  const moreAuthors = reviewedBy ? authors.slice(2) : authors.slice(3);
  const seeMoreAuthors = moreAuthors.length > 0;

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, keys: string[]) => {
    if (keys.includes(event.key)) {
      setIsOpen(false);
      event.preventDefault();
    }
  };

  return (
    <>
      <S.VisibleContributorsWrapper data-cy={dataCy}>
        {visibleContributors.map((contributor) => (
          <Contributor
            key={contributor.uri}
            contributor={contributor}
            type={reviewedBy && contributor.uri === reviewedBy.uri ? 'reviewer' : 'author'}
            trackingData={trackingData}
          />
        ))}
      </S.VisibleContributorsWrapper>
      {seeMoreAuthors && (
        // TODO: FECM-1308 AuthorByLine dropdown should be keyboard accessible
        <S.SeeMoreAuthorsWrapper>
          <S.SeeMoreButton
            onClick={() => setIsOpen(!isOpen)}
            type='button'
            variant='tertiaryBrand'
            arrowType={isOpen ? 'up-arrow' : 'down-arrow'}
            data-cy={COMMON_TEST_IDS.SEE_MORE_AUTHORS_BUTTON}
          >
            SEE MORE
          </S.SeeMoreButton>
          {isOpen && (
            <S.MoreAuthorsDropdown onKeyDown={(e) => handleOnKeyDown(e, ['Escape'])}>
              <S.CloseDropdown
                role='button'
                aria-label='Close menu'
                tabIndex={0}
                onClick={() => setIsOpen(!isOpen)}
                onKeyDown={(e) => handleOnKeyDown(e, ['Enter', ' '])}
                data-cy={COMMON_TEST_IDS.CLOSE_MORE_AUTHORS_BUTTON}
              />
              <S.MoreContributorsWrapper onBlur={() => setIsOpen(!isOpen)}>
                {moreAuthors.map((contributor) => (
                  <Contributor
                    key={contributor.uri}
                    contributor={contributor}
                    type='author'
                    trackingData={trackingData}
                  />
                ))}
              </S.MoreContributorsWrapper>
            </S.MoreAuthorsDropdown>
          )}
        </S.SeeMoreAuthorsWrapper>
      )}
    </>
  );
};

const ContributorsByline = ({ authors, trackingData, dataCy, reviewedBy }: AuthorBylineConfig) => {
  const isMobile = useBreakpoint('mdMax');

  return isMobile ? (
    <CompactByline
      authors={authors}
      reviewedBy={reviewedBy}
      trackingData={trackingData}
      dataCy={dataCy}
    />
  ) : (
    <DesktopByline
      authors={authors}
      reviewedBy={reviewedBy}
      trackingData={trackingData}
      dataCy={dataCy}
    />
  );
};

export default withErrorBoundary(ContributorsByline, { fragment: UIFragments.CONTRIBUTORS_BYLINE });
